<script lang="ts" setup>
import OmniDialog from "@/components/OmniDialog.vue";
import TruckStatus from "@/components/status/TruckStatus.vue";
import TruckLocationInfo from "@/components/truck/TruckLocationInfo.vue";
import TruckMember from "@/components/truck/TruckMember.vue";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import type { TravelOrderDto, TravelOrderPayment } from "@/api/trips/dto/trip.dto";
import type { TruckDriverDto, TruckDto, TruckOwnerDto } from "@/api/trucks/dto/truck.dto";
import type { VForm } from "vuetify/components";
import OmniSelect from "@/components/controls/OmniSelect.vue";
import {
  RouteStopStatus,
  TravelOrderCancelStatuses,
  TravelOrderEditStatusMap,
  TravelOrderStatus,
  TravelOrderStatusMap,
} from "@/data/trip";
import { OrderEditStatusMap, OrderStatus, OrderStatusMap } from "@/data/order";
import OrderFile from "@/views/orders/components/edit/OrderFile.vue";
import { API } from "@/api/api";
import { useSnackbarStore } from "@/store/snackbar.store";
import AlertMessage from "@/components/AlertMessage.vue";
import { useValidation } from "@/composables/useValidation";
import TravelOrderPaymentsPicker from "@/views/trips/components/TravelOrderPaymentsPicker.vue";
import { TravelOrderPaymentStatus, TravelOrderPaymentType } from "@/data/travelOrder";
import { useOrder, useTrip } from "@/composables";
import type { TripRoute, TripRouteStop } from "@/views/trips/edit/trip";
import type { DriverDto } from "@/api/users/dto/driver.dto";
import type { OwnerDto } from "@/api/users/dto/owner.dto";
import UndoFinishDialog from "@/views/trips/view/dialogs/UndoFinishDialog.vue";
import { useError } from "@/composables/useError";

const { requiredValidator } = useValidation();
const { generateId } = useOrder();
const { getRouteDriver, getRouteOwner } = useTrip();
const { scrollToError } = useError();

const isEditDialog = defineModel<boolean>({ default: false });
const travelOrder = defineModel<TravelOrderDto | null>("travelOrder", { default: null });

const props = defineProps<{
  route: TripRoute;
  truck: TruckDto | null;
  travelOrderFromApi: TravelOrderDto | null;
  routeStops: TripRouteStop[];
  travelOrdersByOrder: Record<string, TravelOrderDto[]>;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "update:travelOrder", travelOrder: TravelOrderDto): void;
}>();

const api = new API();
const { showSnackbarError } = useSnackbarStore();

const loading = ref<boolean>(false);
const travelOrderStatusModel = ref<TravelOrderStatus>(travelOrder.value?.status || TravelOrderStatus.inProgress);
const orderStatusModel = ref<OrderStatus>(travelOrder.value?.order.status || OrderStatus.inProgress);
const tonuFile = ref<File | null>(null);
const isOrderDetail = ref<boolean>(false);
const editFormRef = useTemplateRef<VForm | null>("editForm");
const payments = ref<TravelOrderPayment[]>([...(travelOrder.value?.payments || [])]);
const driver = ref<TruckDriverDto | DriverDto | null>(null);
const owner = ref<TruckOwnerDto | OwnerDto | null>(null);
const showUndoFinishDialog = ref(false);

const isTravelOrderStatusDisabled = computed(() => {
  const acceptedStatuses = [RouteStopStatus.inRoute, RouteStopStatus.waitingForLoading, RouteStopStatus.notStarted];

  return props.routeStops.some((routeStop) => !acceptedStatuses.includes(routeStop.status));
});

const travelOrderStatusOptions = computed(() => {
  const dryRunStatuses = [TravelOrderStatus.dryRun, TravelOrderStatus.waitingForDryRun, TravelOrderStatus.noDryRun];

  if (props.travelOrderFromApi && dryRunStatuses.includes(props.travelOrderFromApi.status)) {
    return TravelOrderEditStatusMap.filter((status) => status.id !== TravelOrderStatus.cancelled);
  }

  return TravelOrderEditStatusMap.filter((status) => status.id !== TravelOrderStatus.noDryRun);
});

const selectedStatusName = computed(() => {
  return TravelOrderStatusMap.find((status) => status.id === travelOrderStatusModel.value)?.name;
});

const travelOrdersByOrder = computed<TravelOrderDto[]>(() => {
  return props.travelOrdersByOrder[travelOrder.value?.orderId || ""].map((to) => {
    if (to.id === travelOrder.value?.id) {
      return {
        ...to,
        status: travelOrderStatusModel.value,
      };
    }

    return { ...to };
  });
});

const isOrderStatusDisabled = computed(() => {
  const acceptedStatuses = [
    TravelOrderStatus.dryRun,
    TravelOrderStatus.waitingForDryRun,
    TravelOrderStatus.noDryRun,
    TravelOrderStatus.cancelled,
  ];

  return travelOrdersByOrder.value.some((travelOrder) => !acceptedStatuses.includes(travelOrder.status));
});

const orderStatusOptions = computed(() => {
  const tonuStatuses = [OrderStatus.tonu, OrderStatus.noTonu, OrderStatus.waitingTonu];

  if (props.travelOrderFromApi?.order.status && tonuStatuses.includes(props.travelOrderFromApi.order.status)) {
    return OrderEditStatusMap.filter((status) => status.id !== OrderStatus.cancelled);
  }

  return OrderEditStatusMap.filter((status) => status.id !== OrderStatus.noTonu);
});

const selectedOrderStatusName = computed(() => {
  return OrderStatusMap.find((status) => status.id === orderStatusModel.value)?.name || "";
});

const isUndoProcess = computed(() => {
  return (
    travelOrderStatusModel.value === TravelOrderStatus.inProgress &&
    travelOrder.value?.status !== TravelOrderStatus.inProgress
  );
});

const fileLabel = computed(() => {
  if (isUndoProcess.value) {
    return "Rate confirmation";
  }

  return "TONU file";
});

function deduplicatePaymentsByType(payments: TravelOrderPayment[]): TravelOrderPayment[] {
  const map = new Map<TravelOrderPaymentType, TravelOrderPayment>();

  payments.forEach((payment) => {
    if (!payment.type) {
      return;
    }

    if (!map.has(payment.type)) {
      map.set(payment.type, payment);
    }
  });

  return Array.from(map.values());
}

function updatePaymentsByTravelOrderStatus(status: TravelOrderStatus) {
  if (!travelOrder.value) {
    return;
  }

  let updatedPayments = travelOrder.value.payments;

  if (status === TravelOrderStatus.dryRun || status === TravelOrderStatus.waitingForDryRun) {
    updatedPayments = payments.value.map((payment) => {
      if (payment.type === TravelOrderPaymentType.FlatLinehaul || payment.type === TravelOrderPaymentType.DryRun) {
        return {
          ...payment,
          type: TravelOrderPaymentType.DryRun,
          amount: null,
        };
      }

      return payment;
    });
  } else if (status === TravelOrderStatus.cancelled || status === TravelOrderStatus.noDryRun) {
    updatedPayments = [
      {
        type: TravelOrderPaymentType.DryRun,
        amount: 0,
        status: TravelOrderPaymentStatus.New,
        tempId: generateId(),
      },
    ];
  } else if (status === TravelOrderStatus.inProgress) {
    updatedPayments = payments.value.map((payment) => {
      if (payment.type === TravelOrderPaymentType.DryRun) {
        return {
          type: TravelOrderPaymentType.FlatLinehaul,
          amount: null,
          status: TravelOrderPaymentStatus.New,
          tempId: generateId(),
        };
      }

      return payment;
    });
  }

  payments.value = deduplicatePaymentsByType(updatedPayments);
}

async function editTravelOrder() {
  if (!editFormRef.value || !travelOrder.value) {
    return;
  }

  const result = await editFormRef.value.validate();

  if (!result.valid) {
    scrollToError();
    return;
  }

  const isTOActiveStatus = !TravelOrderCancelStatuses.includes(travelOrderStatusModel.value);
  const wasCancelled = props.travelOrderFromApi && TravelOrderCancelStatuses.includes(props.travelOrderFromApi.status);
  const isUndoFinish = isTOActiveStatus && wasCancelled;

  if (isUndoFinish) {
    showUndoFinishDialog.value = true;
    return;
  }

  loading.value = true;

  if (isOrderDetail.value && tonuFile.value) {
    const fileResponse = await api.files.uploadFile("orders", tonuFile.value, [
      `order:${travelOrder.value.orderId}`,
      OrderStatus.tonu,
    ]);

    if (!fileResponse.success) {
      showSnackbarError("Error uploading TONU file");
      loading.value = false;
      return;
    }
  }

  const updatedTravelOrder: TravelOrderDto = {
    ...travelOrder.value,
    status: travelOrderStatusModel.value,
    order: {
      ...travelOrder.value.order,
      status: orderStatusModel.value,
    },
    payments: payments.value.map((payment) => {
      return {
        ...payment,
        amount: Number(payment.amount),
      };
    }),
  };

  loading.value = false;

  emits("update:modelValue", false);
  emits("update:travelOrder", updatedTravelOrder);
}

async function scrollToFromBottom() {
  await nextTick();

  const formParent = editFormRef.value?.$parent?.$el;

  if (!formParent) {
    return;
  }

  formParent.scrollTo({
    top: formParent.scrollHeight,
    behavior: "smooth",
  });
}

watch(isOrderStatusDisabled, (value, prevValue) => {
  if (!value && prevValue) {
    orderStatusModel.value = OrderStatus.unassigned;
  }
});

watch(travelOrderStatusModel, (value) => {
  updatePaymentsByTravelOrderStatus(value);
});

watch(
  orderStatusModel,
  (value) => {
    if (value === OrderStatus.tonu) {
      isOrderDetail.value = true;
    } else if (value === OrderStatus.noTonu || value === OrderStatus.cancelled) {
      isOrderDetail.value = false;
    } else {
      isOrderDetail.value = false;
    }
  },
  {
    immediate: true,
  },
);

onMounted(async () => {
  driver.value = await getRouteDriver(props.route.driverId, props.truck);
  owner.value = await getRouteOwner(props.route.ownerId, props.truck);
});
</script>

<template>
  <div>
    <omni-dialog
      v-if="travelOrder"
      v-model="isEditDialog"
      :loading="loading"
      :title="`Edit ${travelOrder.name}`"
      :width="800"
      primary-action-label="Save"
      @primary-action:click="editTravelOrder"
    >
      <v-form ref="editForm" class="py-4">
        <div v-if="truck" class="pa-4 mb-5 bg-grey-lighten-4 rounded">
          <div class="d-flex align-center">
            <router-link
              :to="{ name: 'truck-view', params: { id: truck.id } }"
              class="text-primary text-decoration-none font-weight-500"
              data-qa="edit-travel-order-truck-number"
              target="_blank"
            >
              Truck {{ truck.number }}
            </router-link>

            <truck-status :truck="truck" class="ml-3" />
          </div>

          <div class="mt-2">
            <span class="font-size-11 text-grey-darken-1 font-weight-600">Current location</span>

            <truck-location-info :location="truck.currentLocation" :time="truck.currentLocationTime" />
          </div>

          <div class="mt-4">
            <truck-member
              v-if="driver"
              :member="driver"
              class="d-flex align-center"
              hide-status
              label-class="bg-shades-white"
              language-light
            />

            <truck-member
              v-if="owner"
              :member="owner"
              class="d-flex align-center mt-3"
              hide-status
              label-class="bg-shades-white text-green-lighten-1"
              language-light
            />
          </div>
        </div>

        <p class="text-grey-darken-3 font-weight-500">Travel order information</p>

        <div class="d-flex align-center justify-space-between col-gap-4 mt-4">
          <omni-select
            v-model="travelOrderStatusModel"
            :disabled="isTravelOrderStatusDisabled"
            :items="travelOrderStatusOptions"
            :rules="[requiredValidator]"
            data-qa="edit-travel-order-status-field"
            label="Travel order status"
            required
          >
            <template #selection> {{ selectedStatusName }}</template>
          </omni-select>
        </div>

        <alert-message
          v-if="isTravelOrderStatusDisabled"
          bg-color="indigo-lighten-5"
          border-color="none"
          class="mt-4 line-height-16px"
          icon-color="indigo"
        >
          <span class="text-indigo">
            Travel order status can’t be changed, truck has already been loaded at the route stop
          </span>
        </alert-message>

        <div class="mt-5">
          <div class="d-flex align-center col-gap-1">
            <router-link
              :to="{ name: 'order-view', params: { id: travelOrder.orderId } }"
              class="text-decoration-none text-primary"
              target="_blank"
            >
              Order#{{ travelOrder.order.number }}
            </router-link>
            <span class="text-grey-darken-3 font-weight-500"> information:</span>
          </div>

          <div class="d-flex align-center justify-space-between mt-4 col-gap-4">
            <omni-select
              v-model="orderStatusModel"
              :disabled="isOrderStatusDisabled"
              :items="orderStatusOptions"
              :rules="[requiredValidator]"
              data-qa="order-status-select"
              label="Order status"
              required
            >
              <template #selection>
                {{ selectedOrderStatusName }}
              </template>
            </omni-select>
          </div>

          <order-file v-if="isOrderDetail" v-model="tonuFile" :label="fileLabel" class="mt-4"></order-file>
        </div>

        <alert-message
          v-if="isOrderStatusDisabled"
          bg-color="indigo-lighten-5"
          border-color="none"
          class="mt-4 line-height-16px"
          icon-color="indigo"
        >
          <span class="text-indigo"> Order status can’t be changed while it has active travel orders </span>
        </alert-message>

        <travel-order-payments-picker
          v-model="payments"
          :travel-order-status="travelOrderStatusModel"
          class="mt-6"
          @scroll-bottom="scrollToFromBottom"
        />
      </v-form>
    </omni-dialog>

    <undo-finish-dialog
      v-if="showUndoFinishDialog && props.truck"
      v-model="showUndoFinishDialog"
      :route="props.route"
      :truck="props.truck"
      title="Undo finish"
    />
  </div>
</template>

<style lang="scss" scoped>
.travel-number {
  position: relative;
  right: 18px;
}
</style>
